import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { PERM } from '#/config/permissions';
import {
  createMtrActive,
  createMtrDraft,
  fetchMtrDetail,
  updateMtr,
} from '#/api/imsMgmt/settings/mtr';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import LookupRefreshField from '#/modules/commonComponents/form/LookupRefreshField';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import CoverOverlay from '#/modules/commonComponents/CoverOverlay';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import MtrNoSection from '#/modules/imsMgmt/settings/mtr/components/MtrNoSection';
import MtrAssemblyOptionsEditor from '#/modules/imsMgmt/settings/mtr/components/MtrAssemblyOptionsEditor';
import MtrBomLinkSection from '#/modules/imsMgmt/settings/mtr/components/MtrBomLinkSection';
import MtrSpecValuesEditor from '#/modules/imsMgmt/settings/mtr/components/MtrSpecValuesEditor';
import MtrUnitsEditor from '#/modules/imsMgmt/settings/mtr/components/MtrUnitsEditor';
import useMtrForm, {
  buildMtrMutationPayload,
  mapMtrDetailToForm,
} from '#/modules/imsMgmt/settings/mtr/hooks/useMtrForm';
import useMtrStatusMutation, {
  MTR_ACTIVATE_CONFIRM,
  MTR_DEACTIVATE_CONFIRM,
} from '#/modules/imsMgmt/settings/mtr/hooks/useMtrStatusMutation';
import {
  getMtrApiErrorField,
  getMtrApiErrorMessage,
  isMtrVersionConflict,
} from '#/modules/imsMgmt/settings/mtr/helper/apiError';
import {
  canActivateMtr,
  canDeactivateMtr,
  formatMtrStatusLabel,
  MTR_KIND_FILTER_OPTIONS,
} from '#/modules/imsMgmt/settings/mtr/helper/mtrFilterOptions';
import {
  MTR_FIELD_LABELS,
  resolveMtrFieldLabel,
} from '#/modules/imsMgmt/settings/mtr/helper/formValidationLabels';
import { validateMtrSubmit } from '#/modules/imsMgmt/settings/mtr/helper/specValuesSerialize';
import {
  DEFAULT_DISPLAY_UNIT_OPTIONS,
  MTR_FORM_DEFAULT_VALUES,
  mtrFormSchema,
} from '#/modules/imsMgmt/settings/mtr/schemas/mtrSchema';
import {
  mtrDetailQueryKey,
  mtrListQueryKeyPrefix,
  mtrTemplateListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const MTR_TEMPLATE_DETAIL_QUERY_KEY_PREFIX = ['erp', 'mtrTemplate', 'detail'];
const MANUFACTURER_DETAIL_QUERY_KEY_PREFIX = ['erp', 'manufacturer', 'detail'];

const MTR_KIND_LABELS = Object.fromEntries(
  MTR_KIND_FILTER_OPTIONS.map((option) => [option.value, option.label]),
);

const CreateEditMtrPage = () => {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const canActivatePerm = useCan(PERM.MTR_ACTIVATE);
  const canReadMtr = useCan(PERM.MTR_READ);
  const canUpdateMtr = useCan(PERM.MTR_UPDATE);
  const updatedAtRef = useRef(null);
  const submitModeRef = useRef('draft');
  const specBySpecIdRef = useRef({});
  const templateDetailRef = useRef(null);
  const baseUnitInitializedRef = useRef(false);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
    applyManualIssues,
    clearManualIssues,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: mtrDetailQueryKey(id),
    queryFn: () => fetchMtrDetail(id),
    enabled: Boolean(id),
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    mutationFn: async ({ values, mode }) => {
      const payload = buildMtrMutationPayload(
        values,
        id ? updatedAtRef.current : null,
      );

      if (!id && mode === 'draft') {
        return createMtrDraft(payload);
      }

      if (!id && mode === 'active') {
        return createMtrActive(payload);
      }

      return updateMtr(id, payload);
    },
    listQueryKeyPrefix: mtrListQueryKeyPrefix,
    detailQueryKey: mtrDetailQueryKey,
    getEditPath: ROUTES.mtrEdit,
    getSuccessMessage: (result, { isEdit: editing, variables }) => {
      const mode = variables?.mode;
      if (!editing && mode === 'draft') {
        return result.message || '草稿建立成功';
      }
      if (!editing && mode === 'active') {
        return result.message || '物料建立並啟用成功';
      }
      if (editing) {
        return result.message || '更新成功';
      }
      return result.message || '儲存成功';
    },
    getErrorMessage: (error) => {
      const message = getMtrApiErrorMessage(error, '儲存失敗');
      const field = getMtrApiErrorField(error);
      if (field) {
        setError(field, { type: 'server', message });
      }
      return message;
    },
    isVersionConflict: isMtrVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const statusMutation = useMtrStatusMutation({
    id,
    getUpdatedAt: () => updatedAtRef.current,
    onAfterSuccess: async () => {
      await detailQuery.refetch();
    },
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    setError,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(mtrFormSchema),
    defaultValues: MTR_FORM_DEFAULT_VALUES,
  });

  const mtrStructMainId = useWatch({ control, name: 'mtrStructMainId', defaultValue: '' });
  const mtrStructSubId = useWatch({ control, name: 'mtrStructSubId', defaultValue: '' });
  const mtrTemplateId = useWatch({ control, name: 'mtrTemplateId', defaultValue: '' });
  const mtrKind = useWatch({ control, name: 'mtrKind', defaultValue: 'NORMAL' });
  const specEntries = useWatch({ control, name: 'specEntries', defaultValue: [] });

  const loadedDetail = detailQuery.data?.data;

  const {
    treeRows,
    structMainOptions,
    structSubOptions,
    manufacturerOptions,
    manufacturers,
    templateOptions,
    templateDetail,
    specSelectOptionsMap,
    specDetailsById,
    isBootstrapLoading,
    isTemplateListLoading,
    isSpecHydrationLoading,
    buildSpecEntriesFromTemplate: buildEntries,
    refetchStructTree,
    refetchManufacturers,
    refetchTemplateList,
    isStructTreeRefreshing,
    isManufacturersRefreshing,
    isTemplateListRefreshing,
  } = useMtrForm({
    mtrStructMainId,
    mtrStructSubId,
    mtrTemplateId,
    seedSpecOptions: loadedDetail?.specOption ?? [],
  });

  const isGroupMaterial = mtrKind === 'GROUP';

  useEffect(() => {
    if (!mtrTemplateId) {
      templateDetailRef.current = null;
      return;
    }

    if (
      templateDetail
      && String(templateDetail.id) === String(mtrTemplateId)
    ) {
      templateDetailRef.current = templateDetail;
      return;
    }

    templateDetailRef.current = null;
  }, [templateDetail, mtrTemplateId]);

  useEffect(() => {
    if (!loadedDetail || isBootstrapLoading) {
      return;
    }

    updatedAtRef.current = loadedDetail.updatedAt ?? loadedDetail.record?.updatedAt ?? null;
    resetValidationSummary();

    const mapped = mapMtrDetailToForm(loadedDetail, treeRows);
    const bySpecId = mapped._specBySpecId ?? {};
    delete mapped._specBySpecId;
    baseUnitInitializedRef.current = Boolean(mapped._baseUnitInitialized);
    delete mapped._baseUnitInitialized;
    specBySpecIdRef.current = bySpecId;

    const templateId = mapped.mtrTemplateId;
    const currentTemplate = templateDetailRef.current;
    const hasMatchingTemplate = Boolean(
      templateId
      && currentTemplate
      && String(currentTemplate.id) === String(templateId),
    );

    reset({
      ...mapped,
      // 模板已在 cache 時一次帶入；勿只 reset 成 []（儲存後 refetch 會卡在空 entries）
      specEntries: hasMatchingTemplate
        ? buildEntries(currentTemplate, bySpecId)
        : [],
    });
  }, [
    loadedDetail,
    isBootstrapLoading,
    treeRows,
    buildEntries,
    reset,
    resetValidationSummary,
  ]);

  useEffect(() => {
    if (!mtrTemplateId) {
      setValue('specEntries', [], { shouldDirty: false });
      setValue('specCustom', [], { shouldDirty: false });
      return;
    }

    if (!templateDetail) {
      return;
    }

    if (String(templateDetail.id) !== String(mtrTemplateId)) {
      return;
    }

    setValue(
      'specEntries',
      buildEntries(templateDetail, specBySpecIdRef.current),
      { shouldDirty: false },
    );
  }, [templateDetail, mtrTemplateId, buildEntries, setValue]);

  function clearSpecCascadeFields({ shouldDirty = true } = {}) {
    setValue('specEntries', [], { shouldDirty });
    setValue('specCustom', [], { shouldDirty });
    specBySpecIdRef.current = {};
    templateDetailRef.current = null;
  }

  function handleMainChange() {
    setValue('mtrStructSubId', '', { shouldDirty: true, shouldValidate: false });
    setValue('mtrTemplateId', '', { shouldDirty: true, shouldValidate: false });
    clearSpecCascadeFields();
    clearErrors(['mtrStructSubId', 'mtrTemplateId']);
  }

  function handleSubChange() {
    setValue('mtrTemplateId', '', { shouldDirty: true, shouldValidate: false });
    clearSpecCascadeFields();
    clearErrors('mtrTemplateId');
  }

  function handleTemplateChange() {
    clearSpecCascadeFields();
  }

  function handleMtrKindChange(nextKind) {
    if (nextKind === 'GROUP') {
      setValue('manufacturerId', '', { shouldDirty: true, shouldValidate: false });
      setValue('unitBaseId', '', { shouldDirty: true, shouldValidate: false });
      setValue('baseUnitCode', '', { shouldDirty: true, shouldValidate: false });
      setValue('baseUnitName', '', { shouldDirty: true, shouldValidate: false });
      setValue('unitConvert', [], { shouldDirty: true });
      setValue('receiptDefaultQty', '', { shouldDirty: true, shouldValidate: false });
      setValue('defaultDisplayUnit', '', { shouldDirty: true, shouldValidate: false });
      clearErrors([
        'manufacturerId',
        'baseUnitCode',
        'baseUnitName',
        'unitConvert',
        'receiptDefaultQty',
        'defaultDisplayUnit',
      ]);
      return;
    }

    setValue('assemblyOptions', [], { shouldDirty: true });
    setValue('defaultDisplayUnit', 'BASE', { shouldDirty: true, shouldValidate: false });
  }

  function clearStructCascade() {
    setValue('mtrStructMainId', '', { shouldDirty: true, shouldValidate: false });
    setValue('mtrStructSubId', '', { shouldDirty: true, shouldValidate: false });
    setValue('mtrTemplateId', '', { shouldDirty: true, shouldValidate: false });
    clearSpecCascadeFields();
    clearErrors(['mtrStructMainId', 'mtrStructSubId', 'mtrTemplateId', 'specEntries']);
  }

  function clearTemplateCascade() {
    setValue('mtrTemplateId', '', { shouldDirty: true, shouldValidate: false });
    clearSpecCascadeFields();
    clearErrors(['mtrTemplateId', 'specEntries']);
  }

  function clearManufacturerSelection() {
    setValue('manufacturerId', '', { shouldDirty: true, shouldValidate: false });
    clearErrors('manufacturerId');
  }

  async function handleRefreshLookup(refetch, successMessage) {
    const result = await refetch();

    if (result.isError) {
      toast.error('更新失敗，請稍後再試');
      return;
    }

    toast.success(successMessage);
  }

  async function handleRefreshStruct() {
    clearStructCascade();
    await queryClient.invalidateQueries({ queryKey: mtrTemplateListQueryKeyPrefix() });
    await queryClient.invalidateQueries({ queryKey: MTR_TEMPLATE_DETAIL_QUERY_KEY_PREFIX });
    await handleRefreshLookup(refetchStructTree, '分類列表已更新，請重新選擇分類');
  }

  async function handleRefreshTemplate() {
    clearTemplateCascade();
    await queryClient.invalidateQueries({ queryKey: MTR_TEMPLATE_DETAIL_QUERY_KEY_PREFIX });
    await handleRefreshLookup(refetchTemplateList, '模板列表已更新，請重新選擇模板');
  }

  async function handleRefreshManufacturer() {
    clearManufacturerSelection();
    await queryClient.invalidateQueries({ queryKey: MANUFACTURER_DETAIL_QUERY_KEY_PREFIX });
    await handleRefreshLookup(refetchManufacturers, '製造商列表已更新，請重新選擇製造商');
  }

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    const mode = submitModeRef.current;
    const submitValues = {
      ...values,
      _baseUnitInitialized: baseUnitInitializedRef.current,
    };
    const issues = validateMtrSubmit(submitValues, mode);

    if (issues.length > 0) {
      applyManualIssues(setError, clearErrors, issues, {
        formatSummaryMessage: (issue) => {
          const label = MTR_FIELD_LABELS[issue.path]
            ?? resolveMtrFieldLabel(issue.path)
            ?? issue.path;
          return `${label}：${issue.message}`;
        },
      });
      return;
    }

    clearManualIssues(clearErrors);
    save({ values: submitValues, mode });
  }

  function requestSubmit(mode) {
    submitModeRef.current = mode;
    handleSubmit(onSubmit, handleInvalidSubmit)();
  }

  async function handleActivate() {
    const ok = await confirm(MTR_ACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'activate' });
  }

  async function handleDeactivate() {
    const ok = await confirm(MTR_DEACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'deactivate' });
  }

  const pageTitle = isEdit ? '編輯物料' : '新增物料';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const detailStatus = loadedDetail?.status ?? '';
  const hasBaseUnitInitialized = Boolean(
    loadedDetail?.unit?.base?.id ?? loadedDetail?.baseUnit?.id,
  );
  const showActivate = isEdit && canActivatePerm && canActivateMtr(detailStatus);
  const showDeactivate = isEdit && canActivatePerm && canDeactivateMtr(detailStatus);
  const isBusy = isPending || statusMutation.isPending;

  return (
    <PageShell variant="formTall">
      {ConfirmDialogHost}
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header justify-between">
            <div className="flex items-center gap-2">
              <h1 className="page_title mb-0!">
                <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              </h1>
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.mtr}>返回列表</Link>
              </Button>
            </div>

            {isEdit ? (
              <p className="text-sm text-muted-foreground">
                狀態：{formatMtrStatusLabel(detailStatus)}
                {' · '}
                類型：{MTR_KIND_LABELS[mtrKind] ?? mtrKind ?? '—'}
              </p>
            ) : null}
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getMtrApiErrorMessage(detailQuery.error, '找不到物料資料')}
            </p>
          ) : (
            <>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={MTR_FIELD_LABELS}
                fieldLabelResolver={resolveMtrFieldLabel}
                customMessages={customMessages}
              />

              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll relative"
                onSubmit={(event) => event.preventDefault()}
                noValidate
              >
                <CoverOverlay
                  show={isBootstrapLoading}
                  isLoading={isBootstrapLoading}
                />
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="relative w-full pb-5">
                    <div className="form-section__header">
                      <h2 className="form-block__title mb-0! mr-2">基本資料</h2>
                      {showDeactivate ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          disabled={isBusy}
                          onClick={handleDeactivate}
                        >
                          停用物料
                        </Button>
                      ) : null}
                      {showActivate ? (
                        <Button
                          type="button"
                          size="sm"
                          disabled={isBusy}
                          onClick={handleActivate}
                        >
                          啟用物料
                        </Button>
                      ) : null}
                    </div>
                    <div className="relative mt-5 grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
                      <CoverOverlay
                        show={isTemplateListLoading}
                        isLoading={isTemplateListLoading}
                      />
                      <MtrNoSection
                        register={register}
                        errors={errors}
                        watch={watch}
                        setValue={setValue}
                        mtrTemplateId={mtrTemplateId}
                        templateDetail={templateDetail}
                        treeRows={treeRows}
                        manufacturers={manufacturers}
                        specDetailsById={specDetailsById}
                      />

                      <FloatingInput
                        id="name"
                        label="名稱"
                        register={register}
                        errors={errors}
                      />
                      <LookupRefreshField
                        onRefresh={handleRefreshStruct}
                        isRefreshing={isStructTreeRefreshing}
                        ariaLabel="重新載入分類列表"
                      >
                        <FloatingSelect
                          id="mtrStructMainId"
                          label="主分類"
                          options={structMainOptions}
                          control={control}
                          errors={errors}
                          onChange={handleMainChange}
                        />
                      </LookupRefreshField>
                      <FloatingSelect
                        id="mtrStructSubId"
                        label="次分類"
                        options={structSubOptions}
                        control={control}
                        errors={errors}
                        isDisabled={!mtrStructMainId}
                        noOptionsMessage="請先選擇主分類"
                        onChange={handleSubChange}
                      />
                      <LookupRefreshField
                        onRefresh={handleRefreshTemplate}
                        isRefreshing={isTemplateListRefreshing}
                        ariaLabel="重新載入模板列表"
                        disabled={!mtrStructSubId}
                      >
                        <FloatingSelect
                          id="mtrTemplateId"
                          label="物料模板"
                          options={templateOptions}
                          control={control}
                          errors={errors}
                          allowEmpty
                          isClearable
                          isDisabled={!mtrStructSubId}
                          noOptionsMessage="請先選擇次分類，或是先建立對應模板"
                          onChange={handleTemplateChange}
                        />
                      </LookupRefreshField>
                      {!isGroupMaterial ? (
                        <LookupRefreshField
                          onRefresh={handleRefreshManufacturer}
                          isRefreshing={isManufacturersRefreshing}
                          ariaLabel="重新載入製造商列表"
                        >
                          <FloatingSelect
                            id="manufacturerId"
                            label="製造商"
                            options={manufacturerOptions}
                            control={control}
                            errors={errors}
                          />
                        </LookupRefreshField>
                      ) : null}
                      <FloatingInput
                        id="baseUnitPrice"
                        label="基礎單價"
                        type="number"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="safetyQty"
                        label="安全庫存"
                        type="number"
                        register={register}
                        errors={errors}
                      />
                      {!isGroupMaterial ? (
                        <>
                          <FloatingInput
                            id="receiptDefaultQty"
                            label="入庫預設數量"
                            type="number"
                            register={register}
                            errors={errors}
                          />
                          <FloatingSelect
                            id="defaultDisplayUnit"
                            label="預設顯示單位"
                            options={DEFAULT_DISPLAY_UNIT_OPTIONS}
                            control={control}
                            errors={errors}
                          />
                        </>
                      ) : null}
                      {!isEdit ? (
                        <FloatingSelect
                          id="mtrKind"
                          label="物料類型"
                          options={MTR_KIND_FILTER_OPTIONS}
                          control={control}
                          errors={errors}
                          onChange={handleMtrKindChange}
                        />
                      ) : null}
                      <FloatingTextarea
                        id="remark"
                        label="備註"
                        register={register}
                        errors={errors}
                      />
                      <FloatingTextarea
                        id="originalSerialListText"
                        label="原廠料號（逗號或換行分隔）"
                        register={register}
                        errors={errors}
                      />
                    </div>
                  </section>

                  {!isGroupMaterial ? (
                    <section className="relative w-full pb-5">
                      <MtrUnitsEditor
                        mtrId={isEdit ? id : null}
                        canRead={canReadMtr}
                        canUpdate={canUpdateMtr}
                        control={control}
                        register={register}
                        errors={errors}
                        baseUnitInitialized={hasBaseUnitInitialized}
                        onAfterActivate={() => detailQuery.refetch()}
                      />
                    </section>
                  ) : null}

                  {isGroupMaterial ? (
                    <section className="relative w-full pb-5">
                      <MtrAssemblyOptionsEditor
                        control={control}
                        excludeMtrId={isEdit ? id : null}
                      />
                    </section>
                  ) : null}

                  <section className="relative w-full pb-5">
                    <CoverOverlay
                      show={isSpecHydrationLoading}
                      isLoading={isSpecHydrationLoading}
                    />
                    <MtrSpecValuesEditor
                      control={control}
                      register={register}
                      errors={errors}
                      specEntries={specEntries}
                      specSelectOptionsMap={specSelectOptionsMap}
                    />
                  </section>

                  <MtrBomLinkSection
                    control={control}
                    setValue={setValue}
                    disabled={isBusy}
                  />
                </div>

                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.mtr}>取消</Link>
                  </Button>

                  {isEdit ? (
                    <Button
                      type="button"
                      disabled={isBusy}
                      onClick={() => requestSubmit('draft')}
                    >
                      {isPending ? '儲存中…' : '儲存'}
                    </Button>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={isBusy}
                        onClick={() => requestSubmit('draft')}
                      >
                        {isPending ? '儲存中…' : '儲存草稿'}
                      </Button>
                      {canActivatePerm ? (
                        <Button
                          type="button"
                          disabled={isBusy}
                          onClick={() => requestSubmit('active')}
                        >
                          {isPending ? '處理中…' : '建立並啟用'}
                        </Button>
                      ) : null}
                    </>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditMtrPage;
